import type { OidcProvider, Session } from "@vericreator/contracts";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

type AuthenticationStatus = "loading" | "anonymous" | "authenticated";

interface AuthenticationContextValue {
  readonly status: AuthenticationStatus;
  readonly session: Session | null;
  readonly identityProviders: readonly OidcProvider[];
  loginWithIdentityProvider(provider: OidcProvider): Promise<void>;
  logout(): Promise<void>;
}

const AuthenticationContext = createContext<AuthenticationContextValue | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthenticationStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [identityProviders, setIdentityProviders] = useState<readonly OidcProvider[]>([]);

  useEffect(() => {
    void api.identityProviders().then(setIdentityProviders).catch(() => setIdentityProviders([]));
    void api
      .refresh()
      .then((nextSession) => {
        setSession(nextSession);
        setStatus("authenticated");
      })
      .catch(() => setStatus("anonymous"));
  }, []);

  const loginWithIdentityProvider = useCallback(async (provider: OidcProvider) => {
    const authorization = await api.beginIdentityAuthorization(provider);
    window.location.assign(authorization.authorizationUrl);
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setSession(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthenticationContextValue>(
    () => ({ status, session, identityProviders, loginWithIdentityProvider, logout }),
    [identityProviders, loginWithIdentityProvider, logout, session, status]
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
}

export function useAuthentication(): AuthenticationContextValue {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error("useAuthentication must be used inside AuthProvider");
  }
  return context;
}
